import { useCallback, useRef, useState } from "react";
import { useWsSubscription } from "hooks/useWsSubscription";
import type { AddOnStatus } from "pages/media/types";
import type { TabKey } from "@libertify/experience-viewer";
import {
  NOTIFICATIONS_EVENT_TYPE,
  type WSEvent,
} from "../../../store/websocketStore";
import type { ReadyToast } from "./useAddOnStatuses";

// MediaStatus (backend) → the video tab status the UI drives loading/error from.
// Draft is "idle / not requested" — treated as unknown (no loading, no error).
const MEDIA_STATUS_TO_TAB: Record<string, AddOnStatus | undefined> = {
  Draft: undefined,
  Pending: "Pending",
  Requested: "Pending",
  Generating: "Generating",
  Ready: "Generated",
  Uploaded: "Generated",
  Error: "Error",
  Cancelled: "Error",
};

const isMediaReady = (status: string): boolean =>
  status === "Ready" || status === "Uploaded";

export interface UseMediaStatusArgs {
  projectId: string;
  /** The project's own media status (not the publication's) — stays fresh
   *  across a regeneration, without needing a republish. */
  mediaStatus?: string;
  /** Whether the video url already exists (project media, not publication). */
  videoReady: boolean;
}

export interface UseMediaStatusResult {
  /** The `"video"` tab's status (drives its loading/error). */
  videoStatus: AddOnStatus | undefined;
  /** One "video ready" toast, emitted once when the media becomes ready. */
  readyToasts: ReadyToast[];
  dismissToast: (id: number) => void;
  /**
   * Contains `"video"` once the media genuinely transitioned into ready this
   * mount (not already ready on load) — see `justReadyTabs` in
   * `useAddOnStatuses` for why this gates the unread green dot.
   */
  justReadyTabs: Set<TabKey>;
}

/**
 * The video tab's status — the video is the project MEDIA, not an add-on, so
 * it has its own lifecycle. The status is DERIVED from the project's own
 * media status (kept fresh by React Query's normal invalidation, not the
 * publication refetch — see MED-6969), and a single "video ready" toast fires
 * on the `MediaStatusChanged` ready event. No `media_id` filtering: the caller
 * already resolves media for this instance (matching v1).
 */
export function useMediaStatus({
  projectId,
  mediaStatus,
  videoReady,
}: UseMediaStatusArgs): UseMediaStatusResult {
  // `mediaStatus` is authoritative once known — it's what actually flips
  // Generating → Ready on a regeneration. `videoReady` is only a fallback for
  // before the project fetch has resolved at all, same as `useAddOnStatuses`'
  // `baselineStatus` falls back to readyAddOns rather than overriding a live
  // status (see MED-6969: a stale URL from a previous generation must not be
  // able to report "Generated" while a regeneration is still in progress).
  let videoStatus: AddOnStatus | undefined;
  if (mediaStatus) {
    videoStatus = MEDIA_STATUS_TO_TAB[mediaStatus];
  } else if (videoReady) {
    videoStatus = "Generated";
  }

  const [readyToasts, setReadyToasts] = useState<ReadyToast[]>([]);
  // Once toasted, never re-toast. Not seeded from videoReady (that value is
  // false at mount while the publication loads); the `!videoReady` check below
  // is evaluated at EVENT time (it's a handler dep), so an experience whose
  // video already exists never toasts on a later media event for the project
  // (another instance publishing, a regeneration elsewhere).
  const toastedRef = useRef(false);
  // Reactive twin of toastedRef — never cleared by dismissing the toast, so
  // the unread green dot stays eligible after the toast is gone.
  const [justReadyTabs, setJustReadyTabs] = useState<Set<TabKey>>(
    () => new Set()
  );

  const handler = useCallback(
    (event: WSEvent) => {
      if (event.event !== NOTIFICATIONS_EVENT_TYPE.MediaStatusChanged) return;
      if (event.project_id !== projectId) return;
      if (isMediaReady(event.status) && !videoReady && !toastedRef.current) {
        toastedRef.current = true;
        // Negative id → never collides with the add-on toast ids.
        setReadyToasts([{ id: -1, tabKey: "video" }]);
        setJustReadyTabs(new Set(["video"]));
      }
    },
    [projectId, videoReady]
  );

  useWsSubscription({ type: "project", id: projectId }, handler);

  const dismissToast = useCallback((id: number) => {
    setReadyToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  return { videoStatus, readyToasts, dismissToast, justReadyTabs };
}
